import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { PssAuthService } from './pss-auth.service';

export const FEATURE_FLAGS = {
  ADVANCED_BATTLES: 'advanced_battles',
  BOOST_GAUGE:      'boost_gauge',
  FLEET_ANALYTICS:  'fleet_analytics',
  GALAXY_TRACKER:   'galaxy_tracker',
  AI_RULES_EDITOR:  'ai_rules_editor',
  FLEET_MANAGEMENT: 'fleet_management',
  AUTOMATION:           'automation',
  BASIC_PLUS:           'basic_plus',
  REDEEM_CODES:         'redeem_codes',
  TOURNAMENT_ANALYSIS:  'tournament_analysis',
  HIT_LIST:             'hit_list',
} as const;

export type FeatureFlagKey = typeof FEATURE_FLAGS[keyof typeof FEATURE_FLAGS];

export interface FeatureFlag {
  flagKey: string;
  displayName: string;
  description: string;
  requiredTierRank: number;
  isEnabled: boolean;
  userGrantCount?: number;
  fleetGrantCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface UserGrant {
  id: number;
  flagKey: string;
  userId: string;
  userName?: string;
  fleetName?: string;
  note?: string;
  createdAt: string;
}

export interface FleetGrant {
  id: number;
  flagKey: string;
  allianceId: string;
  fleetName?: string;
  note?: string;
  createdAt: string;
}

export interface GrantGroup {
  id: number;
  name: string;
  note?: string;
  entryCount: number;
  createdAt: string;
}

export interface GrantGroupEntry {
  id: number;
  groupId: number;
  entryType: 'user' | 'fleet';
  entryId: string;
  entryName?: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  /** Resolved flag map for the current user — populated by loadForUser(). */
  private _flags: Record<string, boolean> = {};
  private _loaded = false;

  get loaded(): boolean {
    return this._loaded;
  }

  private get isAdmin(): boolean {
    return !!this.auth?.currentSession?.isAdmin;
  }

  constructor(private http: HttpClient, @Optional() private auth?: PssAuthService) {}

  /**
   * Fetch and cache the effective flags for the given PSS user.
   * The allianceId (numeric string) is used for fleet-based grants.
   * Returns an Observable that resolves to the flag map.
   */
  loadForUser(userId: string, allianceId = ''): Observable<Record<string, boolean>> {
    const params: Record<string, string> = { userId };
    if (allianceId) params['allianceId'] = allianceId;
    const qs = new URLSearchParams(params).toString();
    return this.http
      .get<{ flags: Record<string, boolean> }>(`/api/feature-flags?${qs}`)
      .pipe(
        map(res => res.flags ?? {}),
        tap(flags => { this._flags = flags; this._loaded = true; }),
        catchError(() => of({}))
      );
  }

  /**
   * Check if a feature is enabled for the currently loaded user.
   * Returns false when flags have not been loaded yet.
   */
  isEnabled(flagKey: string): boolean {
    if (this.isAdmin) {
      return true;
    }
    return this._loaded ? (this._flags[flagKey] ?? false) : false;
  }

  isBasicPlus(): boolean {
    if (this.isAdmin) {
      return true;
    }
    // Treat advanced features as OFF until flags resolve, to avoid exposing actions before permissions are known.
    return this.loaded ? this.isEnabled(FEATURE_FLAGS.BASIC_PLUS) : false;
  }

  isRestrictedMode(): boolean {
    if (this.isAdmin) {
      return false;
    }
    // 'Basic Plus': if enabled, extra features are unlocked; otherwise, restricted mode is active.
    if (!this.loaded) return true;
    return !this.isEnabled(FEATURE_FLAGS.BASIC_PLUS);
  }

  isLocked(flagKey: string): boolean {
    if (this.isAdmin) {
      return false;
    }
    return !this.isEnabled(flagKey);
  }

  /** Clear cached flags (call on logout). */
  clear(): void {
    this._flags  = {};
    this._loaded = false;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Admin API calls
  // ──────────────────────────────────────────────────────────────────────────

  listFlags(adminUserId: string): Observable<{ flags: FeatureFlag[] }> {
    return this.http.get<{ flags: FeatureFlag[] }>('/api/admin/feature-flags', {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  createFlag(adminUserId: string, flag: { flagKey: string; displayName: string; description?: string; requiredTierRank?: number; isEnabled?: boolean }): Observable<{ flag: FeatureFlag }> {
    return this.http.post<{ flag: FeatureFlag }>('/api/admin/feature-flags', flag, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  updateFlag(adminUserId: string, flagKey: string, patch: Partial<Pick<FeatureFlag, 'displayName' | 'description' | 'requiredTierRank' | 'isEnabled'>>): Observable<{ flag: FeatureFlag }> {
    return this.http.patch<{ flag: FeatureFlag }>(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}`, patch, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  deleteFlag(adminUserId: string, flagKey: string): Observable<{ deleted: string }> {
    return this.http.delete<{ deleted: string }>(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  listUserGrants(adminUserId: string, flagKey: string): Observable<{ grants: UserGrant[] }> {
    return this.http.get<{ grants: UserGrant[] }>(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}/user-grants`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  addUserGrant(adminUserId: string, flagKey: string, userId: string, note?: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}/user-grants`, { userId, note }, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  removeUserGrant(adminUserId: string, flagKey: string, userId: string): Observable<unknown> {
    return this.http.delete(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}/user-grants/${encodeURIComponent(userId)}`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  listFleetGrants(adminUserId: string, flagKey: string): Observable<{ grants: FleetGrant[] }> {
    return this.http.get<{ grants: FleetGrant[] }>(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}/fleet-grants`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  addFleetGrant(adminUserId: string, flagKey: string, allianceId: string, fleetName?: string, note?: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}/fleet-grants`, { allianceId, fleetName, note }, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  removeFleetGrant(adminUserId: string, flagKey: string, allianceId: string): Observable<unknown> {
    return this.http.delete(`/api/admin/feature-flags/${encodeURIComponent(flagKey)}/fleet-grants/${encodeURIComponent(allianceId)}`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  searchUsers(adminUserId: string, query: string): Observable<{ accounts: { userId: string; userName: string; fleetName: string }[] }> {
    return this.http.get<{ accounts: any[] }>('/api/admin/accounts', {
      headers: { 'X-User-Id': adminUserId },
      params: { search: query, activeOnly: '1' },
    });
  }

  searchPssUsers(adminUserId: string, query: string, accessToken: string, server: string): Observable<{ accounts: any[] }> {
    const token = encodeURIComponent(accessToken);
    return this.http.get(
      `/pss/${server}/UserService/SearchUsers?searchString=${encodeURIComponent(query)}&skip=0&take=25&accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(
      map((xml: string) => {
        const doc = new DOMParser().parseFromString(xml, 'text/xml');
        const accounts = Array.from(doc.querySelectorAll('User')).map(el => ({
          userId:      el.getAttribute('Id')        || el.getAttribute('UserId')   || '',
          userName:    el.getAttribute('Name')      || el.getAttribute('UserName') || '',
          allianceId:  el.getAttribute('AllianceId') || el.getAttribute('GuildId') || '',
          fleetName:   '',
        })).filter((a: any) => !!a.userId);
        return { accounts };
      }),
      catchError(() => of({ accounts: [] }))
    );
  }

  searchFleets(adminUserId: string, query: string): Observable<{ accounts: { userId: string; userName: string; fleetName: string }[] }> {
    return this.http.get<{ accounts: any[] }>('/api/admin/accounts', {
      headers: { 'X-User-Id': adminUserId },
      params: { fleet: query, activeOnly: '1' },
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Grant groups admin API calls
  // ──────────────────────────────────────────────────────────────────────────

  listGrantGroups(adminUserId: string): Observable<{ groups: GrantGroup[] }> {
    return this.http.get<{ groups: GrantGroup[] }>('/api/admin/grant-groups', {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  createGrantGroup(adminUserId: string, name: string, note?: string): Observable<{ group: GrantGroup }> {
    return this.http.post<{ group: GrantGroup }>('/api/admin/grant-groups', { name, note }, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  deleteGrantGroup(adminUserId: string, groupId: number): Observable<unknown> {
    return this.http.delete(`/api/admin/grant-groups/${groupId}`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  listGroupEntries(adminUserId: string, groupId: number): Observable<{ entries: GrantGroupEntry[] }> {
    return this.http.get<{ entries: GrantGroupEntry[] }>(`/api/admin/grant-groups/${groupId}/entries`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  addGroupEntry(adminUserId: string, groupId: number, entryType: 'user' | 'fleet', entryId: string, entryName?: string): Observable<{ entry: GrantGroupEntry }> {
    return this.http.post<{ entry: GrantGroupEntry }>(`/api/admin/grant-groups/${groupId}/entries`, { entryType, entryId, entryName }, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  removeGroupEntry(adminUserId: string, groupId: number, entryId: number): Observable<unknown> {
    return this.http.delete(`/api/admin/grant-groups/${groupId}/entries/${entryId}`, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  applyGroupsToFlags(adminUserId: string, groupIds: number[], flagKeys: string[]): Observable<{ added: number; errors?: string[] }> {
    return this.http.post<{ added: number; errors?: string[] }>('/api/admin/grant-groups/apply', { groupIds, flagKeys }, {
      headers: { 'X-User-Id': adminUserId },
    });
  }

  searchPssFleets(adminUserId: string, query: string, accessToken: string, server: string): Observable<{ accounts: any[] }> {
    const token = encodeURIComponent(accessToken);
    return this.http.get(
      `/pss/${server}/AllianceService/SearchAlliances?name=${encodeURIComponent(query)}&skip=0&take=25&accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(
      map((xml: string) => {
        const doc = new DOMParser().parseFromString(xml, 'text/xml');
        const accounts = Array.from(doc.querySelectorAll('Alliance')).map(el => ({
          allianceId:   el.getAttribute('AllianceId') || el.getAttribute('Id') || '',
          allianceName: el.getAttribute('AllianceName') || el.getAttribute('Name') || '',
          memberCount:  el.getAttribute('AllianceMemberCount') || el.getAttribute('MemberCount') || '',
          trophy:       el.getAttribute('Trophy') || '',
        })).filter((a: any) => !!a.allianceId);
        return { accounts };
      }),
      catchError(() => of({ accounts: [] }))
    );
  }
}
